/*
 * MapView — Vanilla Leaflet wrapper for Vendidirect map catalog.
 *
 * Responsibilities:
 *   - Render map with light/dark tiles based on data-theme
 *   - Draw blue price pins with hover state
 *   - Handle popup on pin click (photo + price + link)
 *   - Freehand-ish polygon drawing (click-to-add-vertex, close, delete)
 *   - Emit `onViewportChange` after move/zoom (debounced) for search-as-I-move
 *   - Emit `onPolygonComplete(coords)` when user closes a polygon
 *
 * Props:
 *   properties: [{ id, latitude, longitude, price, title, city, surface_sqm, rooms, photos, transaction_type }]
 *   center: [lat, lng]  initial center
 *   zoom: number        initial zoom
 *   activeId?: string   highlight one pin
 *   drawingEnabled: bool
 *   polygon: [[lat,lng],...]  (controlled)
 *   onPolygonComplete: (coords|null) => void
 *   onViewportChange: ({center, zoom, bbox}) => void
 *   onPinClick: (id) => void
 *   theme: "light" | "dark"
 */
import React, { useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./MapCatalog.css";

// Marker icon paths (default icons not used by us but leaflet needs them not-broken)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION = '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

function fmtPrice(price, transaction) {
    if (!price) return "—";
    if (transaction === "affitto") {
        if (price >= 1000) return `€${(price / 1000).toFixed(price % 1000 === 0 ? 0 : 1)}k/mo`;
        return `€${Math.round(price)}/mo`;
    }
    if (price >= 1_000_000) return `€${(price / 1_000_000).toFixed(2)}M`;
    if (price >= 1000) return `€${Math.round(price / 1000)}k`;
    return `€${Math.round(price)}`;
}

function escapeHtml(value) {
    return String(value || "").replace(/[&<>'"]/g, (char) => ({
        "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;",
    }[char]));
}

function safeHref(value) {
    if (!value) return "";
    try {
        const url = new URL(value, window.location.origin);
        return ["http:", "https:"].includes(url.protocol) ? url.href : "";
    } catch {
        return "";
    }
}

function buildPopupHtml(p) {
    const cover = safeHref((p.photos && p.photos[0]) || "");
    const price = fmtPrice(p.price, p.transaction_type);
    const meta = [
        p.city ? p.city.charAt(0).toUpperCase() + p.city.slice(1) : null,
        p.rooms ? `${p.rooms} locali` : null,
        p.surface_sqm ? `${p.surface_sqm} m²` : null,
    ].filter(Boolean).join(" · ");
    const external = safeHref(p.external_url);
    const href = external || `/annunci/${encodeURIComponent(p.id)}`;
    const target = external ? ' target="_blank" rel="noopener noreferrer"' : "";
    return `
    <div class="vd-popup-card">
      ${cover ? `<img src="${escapeHtml(cover)}" alt="" />` : ""}
      <div class="vd-popup-body">
        <div class="vd-popup-price">${escapeHtml(price)}</div>
        <div class="vd-popup-title">${escapeHtml(p.title)}</div>
        <div class="vd-popup-meta">${escapeHtml(meta)}</div>
        <a class="vd-popup-cta" href="${escapeHtml(href)}"${target} data-testid="map-popup-cta-${escapeHtml(p.id)}">${external ? "Apri fonte" : "Vedi annuncio"} →</a>
      </div>
    </div>`;
}

export default function MapView({
    properties = [],
    center = [45.4642, 9.19],
    zoom = 12,
    activeId = null,
    drawingEnabled = false,
    polygon = null,
    onPolygonComplete,
    onViewportChange,
    onPinClick,
    theme = "dark",
    polygonColor = "#D4AF6A",
    focusKey = null,
    testId = "map-catalog-map",
}) {
    const wrapperRef = useRef(null);
    const mapRef = useRef(null);
    const tileRef = useRef(null);
    const markersRef = useRef(new Map()); // id -> L.marker
    const polygonLayerRef = useRef(null); // finished polygon polygon
    const draftLineRef = useRef(null);    // draft polyline
    const draftVerticesRef = useRef([]);  // [[lat, lng], ...]
    const draftMarkersRef = useRef([]);   // small circle markers per vertex
    const drawingRef = useRef(false);
    const viewportDebounceRef = useRef(null);

    // Emit viewport (debounced)
    const emitViewport = useCallback(() => {
        if (!mapRef.current || !onViewportChange) return;
        if (viewportDebounceRef.current) clearTimeout(viewportDebounceRef.current);
        viewportDebounceRef.current = setTimeout(() => {
            const m = mapRef.current;
            if (!m) return;
            const c = m.getCenter();
            const b = m.getBounds();
            onViewportChange({
                center: [c.lat, c.lng],
                zoom: m.getZoom(),
                bbox: [b.getSouth(), b.getWest(), b.getNorth(), b.getEast()],
            });
        }, 400);
    }, [onViewportChange]);
    const initialMapPropsRef = useRef({ center, zoom, emitViewport });

    // ---- Init map once ----
    useEffect(() => {
        if (!wrapperRef.current || mapRef.current) return;
        const initial = initialMapPropsRef.current;
        const map = L.map(wrapperRef.current, {
            center: initial.center,
            zoom: initial.zoom,
            zoomControl: true,
            scrollWheelZoom: true,
            worldCopyJump: true,
        });
        mapRef.current = map;
        tileRef.current = L.tileLayer(TILE_URL, {
            attribution: TILE_ATTRIBUTION,
            subdomains: "abc",
            maxZoom: 19,
        }).addTo(map);
        map.on("moveend zoomend", initial.emitViewport);
        // initial viewport emit
        setTimeout(initial.emitViewport, 100);
        return () => {
            map.off("moveend zoomend", initial.emitViewport);
            map.remove();
            mapRef.current = null;
        };
        // Init-once effect. Center/zoom changes handled by other effects below.
        // Theme (light/dark) is applied purely via CSS filter on .leaflet-tile-pane
        // (see MapCatalog.css) — OpenStreetMap standard tiles require no API key
        // and have a single style, so no tile-layer swap is needed on theme change.
    }, []);

    // ---- Sync markers ----
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        const currentIds = new Set(properties.map((p) => p.id));
        // remove stale
        markersRef.current.forEach((m, id) => {
            if (!currentIds.has(id)) {
                map.removeLayer(m);
                markersRef.current.delete(id);
            }
        });
        // add / update
        properties.forEach((p) => {
            if (p.latitude == null || p.longitude == null) return;
            const priceLabel = fmtPrice(p.price, p.transaction_type);
            const isLuxury = p.price >= 800000;
            const isActive = p.id === activeId;
            const icon = L.divIcon({
                className: "",
                html: `<div class="vd-price-pin ${isLuxury ? "is-luxury" : ""} ${isActive ? "is-active" : ""}" data-testid="map-pin-${p.id}">${priceLabel}</div>`,
                iconSize: null,
                iconAnchor: [22, 12],
            });
            let marker = markersRef.current.get(p.id);
            if (marker) {
                marker.setLatLng([p.latitude, p.longitude]);
                marker.setIcon(icon);
            } else {
                marker = L.marker([p.latitude, p.longitude], { icon }).addTo(map);
                marker.bindPopup(() => buildPopupHtml(p), { closeButton: true, autoPan: true, offset: [0, -6] });
                marker.on("click", () => { if (onPinClick) onPinClick(p.id); });
                markersRef.current.set(p.id, marker);
            }
        });
    }, [properties, activeId, onPinClick]);

    // ---- Polygon (controlled) → render ----
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        if (polygonLayerRef.current) {
            map.removeLayer(polygonLayerRef.current);
            polygonLayerRef.current = null;
        }
        if (polygon && polygon.length >= 3) {
            polygonLayerRef.current = L.polygon(polygon, {
                color: polygonColor,
                weight: 2,
                opacity: 0.95,
                fillColor: polygonColor,
                fillOpacity: 0.18,
                dashArray: "6 6",
            }).addTo(map);
        }
    }, [polygon, polygonColor]);

    const focusPropsRef = useRef({ polygon, center, zoom });
    focusPropsRef.current = { polygon, center, zoom };
    useEffect(() => {
        const map = mapRef.current;
        if (!map || focusKey == null) return;
        const current = focusPropsRef.current;
        if (current.polygon?.length >= 3) {
            map.fitBounds(L.latLngBounds(current.polygon), { padding: [36, 36], maxZoom: 15 });
        } else {
            map.setView(current.center, current.zoom);
        }
    }, [focusKey]);

    // ---- Drawing mode logic ----
    const clearDraft = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;
        if (draftLineRef.current) { map.removeLayer(draftLineRef.current); draftLineRef.current = null; }
        draftMarkersRef.current.forEach((m) => map.removeLayer(m));
        draftMarkersRef.current = [];
        draftVerticesRef.current = [];
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        drawingRef.current = drawingEnabled;
        if (!drawingEnabled) {
            // Leaving drawing mode: clear any unfinished draft
            clearDraft();
            map.getContainer().style.cursor = "";
            return;
        }
        map.getContainer().style.cursor = "crosshair";
        const onClick = (e) => {
            if (!drawingRef.current) return;
            const { lat, lng } = e.latlng;
            draftVerticesRef.current.push([lat, lng]);
            // small vertex marker
            const vm = L.circleMarker([lat, lng], {
                radius: 5, color: polygonColor, weight: 2, fillColor: "#ffffff", fillOpacity: 1,
            }).addTo(map);
            draftMarkersRef.current.push(vm);
            const coords = draftVerticesRef.current;
            if (coords.length === 1) {
                // start a polyline
                draftLineRef.current = L.polyline([coords[0]], {
                    color: polygonColor, weight: 2, opacity: 0.9, dashArray: "4 6",
                }).addTo(map);
            } else if (draftLineRef.current) {
                draftLineRef.current.setLatLngs(coords);
            }
            // First vertex clicked twice (near itself) → close polygon
            if (coords.length >= 4) {
                const first = coords[0];
                const dist = map.distance(first, [lat, lng]);
                if (dist < 50) {
                    // close: use vertices except the last duplicate
                    const finalCoords = coords.slice(0, -1);
                    clearDraft();
                    if (onPolygonComplete) onPolygonComplete(finalCoords);
                }
            }
        };
        map.on("click", onClick);
        return () => {
            map.off("click", onClick);
            map.getContainer().style.cursor = "";
        };
    }, [drawingEnabled, onPolygonComplete, clearDraft, polygonColor]);

    // Expose imperative "close now" / "clear draft" (button) via custom events on the wrapper
    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const onFinish = () => {
            if (draftVerticesRef.current.length >= 3) {
                const coords = [...draftVerticesRef.current];
                clearDraft();
                if (onPolygonComplete) onPolygonComplete(coords);
            }
        };
        // Silent draft clear (no onPolygonComplete call) — used when the parent
        // programmatically resets drawing state without wanting to fire a null completion.
        const onClearDraft = () => { clearDraft(); };
        el.addEventListener("vd:finish-polygon", onFinish);
        el.addEventListener("vd:clear-draft", onClearDraft);
        return () => {
            el.removeEventListener("vd:finish-polygon", onFinish);
            el.removeEventListener("vd:clear-draft", onClearDraft);
        };
    }, [clearDraft, onPolygonComplete]);

    return (
        <div ref={wrapperRef} className="vd-map-root" data-testid={testId} data-theme={theme} />
    );
}
